// Cosmetic accessories (progression unlocks), drawn in code on top of a creature.
import type { Graphics } from "pixi.js";
import type { Species } from "@eb/shared";
import { INK, OKABE_ITO } from "../palette";
import type { AccessoryKind } from "./pose";
import type { Geo } from "./anatomy";
import { blob, disc, inkLine, oval, traceDome } from "./draw";

/** Accent cloth color that contrasts with the species color. */
function clothFor(species: Species): number {
  switch (species) {
    case "snapper":
    case "thumper":
    case "chirper":
      return OKABE_ITO.blue;
    case "clapper":
    case "critter":
      return OKABE_ITO.vermillion;
    case "wobbler":
      return OKABE_ITO.bluishGreen;
    case "glimmer":
      return OKABE_ITO.reddishPurple;
    case "woodwump":
    case "drawbear":
    case "operatoad":
      return OKABE_ITO.skyBlue;
    case "acidmite":
      return OKABE_ITO.vermillion;
    case "choruspede":
      return OKABE_ITO.yellow;
    case "boomer":
      return OKABE_ITO.skyBlue;
  }
}

/** Draw `kind` into `g` (cleared first) using the creature's face geometry. */
export function drawAccessory(
  g: Graphics,
  kind: AccessoryKind,
  geo: Geo,
  H: number,
  ow: number,
  species: Species,
  eyeCount: number,
): void {
  g.clear();
  const top = geo.headTopY;
  const hw = Math.min(geo.headW * 0.5, H * 0.36);
  const cloth = clothFor(species);
  switch (kind) {
    case "none":
      return;
    case "top_hat": {
      const w = hw * 0.62,
        h = H * 0.3,
        brimY = top + H * 0.05;
      g.roundRect(-w, brimY - h, w * 2, h, H * 0.03)
        .fill({ color: 0x2b2540 })
        .stroke({ width: ow, color: INK });
      g.rect(-w, brimY - h * 0.35, w * 2, h * 0.22).fill({ color: cloth });
      oval(g, 0, brimY, hw * 1.05, H * 0.045, 0x2b2540, ow);
      return;
    }
    case "beanie": {
      const w = hw * 1.0,
        y = top + H * 0.07;
      traceDome(g, 0, y, w).fill({ color: cloth }).stroke({ width: ow, color: INK });
      g.roundRect(-w * 1.05, y - H * 0.06, w * 2.1, H * 0.08, H * 0.04)
        .fill({ color: OKABE_ITO.yellow })
        .stroke({ width: ow, color: INK });
      disc(g, 0, y - w - H * 0.03, H * 0.06, 0xffffff, ow);
      return;
    }
    case "scarf": {
      const y = geo.neckY,
        w = geo.neckHalfW * 1.04,
        th = H * 0.1;
      // Hanging end first (behind the band), then the band.
      blob(g, [w * 0.3, y, w * 0.55, y, w * 0.6, y + H * 0.26, w * 0.32, y + H * 0.24], cloth, ow, false);
      g.roundRect(-w, y - th / 2, w * 2, th, th / 2)
        .fill({ color: cloth })
        .stroke({ width: ow, color: INK });
      for (let i = -2; i <= 2; i++)
        inkLine(g, [i * w * 0.35, y - th * 0.3, i * w * 0.35 + th * 0.3, y + th * 0.3], ow * 0.5, false, 0xffffff);
      return;
    }
    case "glasses": {
      const r = geo.eyeR * 1.35,
        y = geo.eyeY;
      const frame = { width: ow * 1.1, color: INK };
      if (eyeCount === 1) {
        g.circle(0, y, geo.eyeR * 1.6)
          .fill({ color: 0xcfefff, alpha: 0.25 })
          .stroke(frame);
        inkLine(g, [geo.eyeR * 1.4, y + geo.eyeR * 0.8, geo.eyeR * 2, y + H * 0.2], ow * 0.6);
        return;
      }
      for (const s of [-1, 1])
        g.circle(s * geo.eyeDx, y, r)
          .fill({ color: 0xcfefff, alpha: 0.25 })
          .stroke(frame);
      const gap = geo.eyeDx - r;
      if (gap > 0) inkLine(g, [-gap, y - r * 0.2, 0, y - r * 0.4, gap, y - r * 0.2], ow, true);
      for (const s of [-1, 1])
        inkLine(g, [s * (geo.eyeDx + r), y - r * 0.1, s * (geo.eyeDx + r + H * 0.06), y - r * 0.2], ow);
      return;
    }
    case "bow": {
      const x = hw * 0.55,
        y = top + H * 0.04,
        s = H * 0.11;
      blob(g, [x, y, x - s * 1.2, y - s * 0.8, x - s * 1.2, y + s * 0.6], OKABE_ITO.reddishPurple, ow, true);
      blob(g, [x, y, x + s * 1.2, y - s * 0.8, x + s * 1.2, y + s * 0.6], OKABE_ITO.reddishPurple, ow, true);
      disc(g, x, y, s * 0.35, 0xe9bad4, ow);
      return;
    }
    case "crown": {
      const w = hw * 0.7,
        y = top + H * 0.05,
        h = H * 0.17;
      const pts = [-w, y, -w, y - h, -w * 0.5, y - h * 0.55, 0, y - h * 1.15, w * 0.5, y - h * 0.55, w, y - h, w, y];
      blob(g, pts, OKABE_ITO.yellow, ow, false);
      disc(g, 0, y - h * 0.35, H * 0.03, OKABE_ITO.vermillion, ow * 0.6);
      disc(g, -w * 0.55, y - h * 0.25, H * 0.022, OKABE_ITO.skyBlue, ow * 0.6);
      disc(g, w * 0.55, y - h * 0.25, H * 0.022, OKABE_ITO.bluishGreen, ow * 0.6);
      return;
    }
  }
}
